import { useCallback, useMemo, useState } from "react";
import type { Localized } from "../types/i18n";
import type { Validator } from "../utils/validation";

type FieldElement = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

export function useFormValidation<T extends Record<string, string>>(
  initial: T,
  rules: Partial<Record<keyof T, Validator>>
) {
  const [values, setValues] = useState<T>(initial);
  const [touched, setTouched] = useState<Partial<Record<keyof T, boolean>>>({});

  const errors = useMemo(() => {
    const result: Partial<Record<keyof T, Localized>> = {};
    (Object.keys(rules) as (keyof T)[]).forEach((key) => {
      const rule = rules[key];
      const err = rule ? rule(values[key]) : null;
      if (err) result[key] = err;
    });
    return result;
  }, [values, rules]);

  const handleChange = useCallback((e: React.ChangeEvent<FieldElement>) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
  }, []);

  const setValue = useCallback((name: keyof T, value: string) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleBlur = useCallback((e: React.FocusEvent<FieldElement>) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  }, []);

  const visibleError = useCallback(
    (name: keyof T) => (touched[name] ? errors[name] ?? null : null),
    [touched, errors]
  );

  const validateAll = useCallback(() => {
    const all: Partial<Record<keyof T, boolean>> = {};
    (Object.keys(rules) as (keyof T)[]).forEach((k) => (all[k] = true));
    setTouched(all);
    return Object.keys(errors).length === 0;
  }, [rules, errors]);

  const reset = useCallback(() => {
    setValues(initial);
    setTouched({});
  }, [initial]);

  return { values, handleChange, handleBlur, setValue, visibleError, validateAll, reset };
}
